/**
 * Partner registry — every nonprofit or program administrator that gets its own
 * surface on the site. None of these are paid placements.
 */
import { chenoaFund } from "./chenoaFund";
import { naca } from "./naca";
import { neighborworks } from "./neighborworks";
import { operationHope } from "./operationHope";
import { unidosUs } from "./unidosUs";
import { urbanLeague } from "./urbanLeague";
import { PARTNER_SITE, type Partner, type PartnerBarrier } from "./types";

export * from "./types";
export { chenoaFund, naca, neighborworks, operationHope, unidosUs, urbanLeague };

export const PARTNERS: Partner[] = [
  naca,
  operationHope,
  neighborworks,
  unidosUs,
  urbanLeague,
  chenoaFund,
];

export const getPartner = (slug: string): Partner | undefined =>
  PARTNERS.find((p) => p.slug === slug);

/** Organizations we name but don't give a dedicated page — listed on the hub. */
export const ALSO_WORTH_KNOWING: { name: string; what: string; href: string }[] = [
  {
    name: "HUD-approved housing counseling agencies",
    what: "The official directory of free counseling agencies, searchable by ZIP.",
    href: "/tools/find-housing-counselor",
  },
  {
    name: "Habitat for Humanity",
    what: "Affordable homeownership in exchange for sweat equity hours, run by local affiliates.",
    href: "https://www.habitat.org/housing-help",
  },
  {
    name: "NFCC",
    what: "Nonprofit credit counseling and debt management plans for buyers blocked by debt.",
    href: "https://www.nfcc.org/",
  },
  {
    name: "GreenPath and Money Management International",
    what: "Nonprofit credit counseling agencies with national phone and online coverage.",
    href: "https://www.greenpath.com/",
  },
  {
    name: "HomeFree-USA",
    what: "Counseling network focused on first-generation and underserved buyers.",
    href: "https://homefreeusa.org/",
  },
  {
    name: "Fannie Mae HomeView and Freddie Mac CreditSmart",
    what: "Free online homebuyer education courses accepted by many assistance programs.",
    href: "/tools/free-homebuying-readiness-tools",
  },
];

export const PARTNERS_HUB_PATH = "/partners";
export const PARTNERS_HUB_TITLE =
  "Nonprofits and Programs That Help You Buy a Home (2026 Directory)";
export const PARTNERS_HUB_DESCRIPTION =
  "A plain-English directory of the nonprofits and national programs worth knowing about before you buy — organized by the thing actually blocking you: credit, savings, an education certificate, language, or income limits.";

export const HUB_BARRIER_ORDER: PartnerBarrier[] = [
  "credit",
  "savings",
  "income_limits",
  "certificate",
  "language",
];

export const partnersByBarrier = (barrier: PartnerBarrier): Partner[] =>
  PARTNERS.filter((p) => p.barriers.includes(barrier));

export const PARTNERS_HUB_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CollectionPage",
      name: PARTNERS_HUB_TITLE,
      description: PARTNERS_HUB_DESCRIPTION,
      url: `${PARTNER_SITE}${PARTNERS_HUB_PATH}`,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: PARTNER_SITE },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${PARTNER_SITE}/` },
        { "@type": "ListItem", position: 2, name: "Partners", item: `${PARTNER_SITE}${PARTNERS_HUB_PATH}` },
      ],
    },
    {
      "@type": "ItemList",
      itemListElement: PARTNERS.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: p.name,
        url: `${PARTNER_SITE}${p.path}`,
      })),
    },
  ],
};
